export type Category =
  | 'Protein'
  | 'Strength'
  | 'Endurance'
  | 'Health'
  | 'Stress'
  | 'Sleep'

export interface Product {
  id: string
  name: string
  tagline: string
  category: Category
  dose: string
  doseUnit: string
  whyEffective: string
  shortDescription: string
  status: 'coming-soon' | 'available'
  bottleColor: 'charcoal' | 'burgundy' | 'navyslate' | 'tealslate'
  accentColor: 'tealslate' | 'lightteal'
  /** Front-of-bottle photo. Used everywhere as the primary product image. */
  image: string
  /**
   * Back-of-bottle photo (label / supplement facts). Shown as a second image
   * in the product gallery. Placeholder reuses the front photo until real
   * back-of-bottle photography is dropped in — swap each `backImage` URL.
   */
  backImage?: string
  /** Display fallback price in cents — Stripe is source of truth at checkout */
  price: number
  /**
   * Stripe price `lookup_key`. Resolves to whichever active price has this
   * key, in whichever Stripe mode (test/live) is currently used. Changing
   * the price in Stripe Dashboard requires no code redeploy.
   */
  lookupKey?: string
}

// Placeholder product photos — neutral supplement bottle imagery to be
// swapped with real Thorne / Effective Dose product photos.
const PLACEHOLDER_PERFORMANCE =
  'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&q=80'
const PLACEHOLDER_FOUNDATION =
  'https://images.unsplash.com/photo-1626516953739-5c4a59a8c6b4?w=800&q=80'
const PLACEHOLDER_RECOVERY =
  'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80'

export const shopData: Product[] = [
  {
    id: 'protein',
    name: 'Whey Isolate Protein',
    tagline: 'Pure protein. No filler.',
    category: 'Protein',
    dose: '30',
    doseUnit: 'g per scoop',
    whyEffective:
      'Most powders deliver 18-21g. Research supports 25-30g per serving to maximally stimulate muscle protein synthesis — especially critical during a caloric deficit.',
    shortDescription:
      'Cold-processed whey isolate at the dose your muscles actually need to grow and repair.',
    status: 'available',
    bottleColor: 'charcoal',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/mH74f1c9MKKDZ19Ue4AY7Y/ChatGPT_Image_May_10,_2026,_12_22_47_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/mH74f1c9MKKDZ19Ue4AY7Y/ChatGPT_Image_May_10,_2026,_12_22_47_AM.png',
    price: 5499,
    lookupKey: 'whey_isolate_v1',
  },
  {
    id: 'creatine',
    name: 'Creatine Monohydrate',
    tagline: 'The most studied. The most effective.',
    category: 'Strength',
    dose: '5',
    doseUnit: 'g per serving',
    whyEffective:
      'No proprietary blends. No "creatine matrix." Just 5g of pure micronized creatine monohydrate — the dose backed by 700+ clinical trials.',
    shortDescription:
      'The single most evidence-backed supplement for strength, lean mass, and cognition.',
    status: 'available',
    bottleColor: 'tealslate',
    accentColor: 'lightteal',
    image:
      'https://cdn.magicpatterns.com/uploads/pv91jXf4YxBUemMVV4qAbt/creatine_transparent.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/1qZb3Bp1MogiSpL3aDMdaR/file_00000000d5a471f7bf8357df1e2f6ad6.png',
    price: 2999,
    lookupKey: 'creatine_v1',
  },
  {
    id: 'l-citrulline',
    name: 'L-Citrulline',
    tagline: 'Effective doses for real pumps.',
    category: 'Endurance',
    dose: '8',
    doseUnit: 'g per serving',
    whyEffective:
      'Most pre-workouts hide 1-2g of citrulline in proprietary blends. Studies show benefits begin at 6g — we deliver the full clinically effective 8g.',
    shortDescription:
      'Improved blood flow, endurance, and reduced muscle soreness — at the dose research demands.',
    status: 'available',
    bottleColor: 'navyslate',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/8XZQmcPQ6q6454fXsb4jYY/ChatGPT_Image_May_10,_2026,_12_23_01_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/8XZQmcPQ6q6454fXsb4jYY/ChatGPT_Image_May_10,_2026,_12_23_01_AM.png',
    price: 3499,
    lookupKey: 'l_citrulline_v1',
  },
  {
    id: 'beta-alanine',
    name: 'Beta-Alanine',
    tagline: 'Push past the burn.',
    category: 'Endurance',
    dose: '5',
    doseUnit: 'g per serving',
    whyEffective:
      'Clinical research uses 3.2-6.4g daily for measurable performance gains. We deliver 5g — the sweet spot for muscular endurance without underdosing.',
    shortDescription:
      'Buffers muscle acidity for longer, stronger sets in the 60-240 second power range.',
    status: 'available',
    bottleColor: 'burgundy',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/sHNaskXr9pePwx9sAWfwJw/ChatGPT_Image_May_10,_2026,_12_23_06_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/sHNaskXr9pePwx9sAWfwJw/ChatGPT_Image_May_10,_2026,_12_23_06_AM.png',
    price: 2999,
    lookupKey: 'beta_alanine_v1',
  },
  {
    id: 'multivitamin-womens',
    name: "Women's Multivitamin",
    tagline: 'Bioavailable forms. Female-specific dosing.',
    category: 'Health',
    dose: '100%',
    doseUnit: 'RDA + therapeutic',
    whyEffective:
      'Formulated for female physiology with higher iron, methylfolate for reproductive health, and bioavailable B-vitamins. No cyanocobalamin, folic acid, or magnesium oxide — only the forms your body can actually use.',
    shortDescription:
      'A multi designed around what women actually need — not a generic blend with a pink label.',
    status: 'available',
    bottleColor: 'burgundy',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/8e9oaJUiBZBqise9GvFU4G/ChatGPT_Image_May_10,_2026,_12_23_18_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/8e9oaJUiBZBqise9GvFU4G/ChatGPT_Image_May_10,_2026,_12_23_18_AM.png',
    price: 4499,
    lookupKey: 'multi_womens_v1',
  },
  {
    id: 'multivitamin-mens',
    name: "Men's Multivitamin",
    tagline: 'Bioavailable forms. Male-specific dosing.',
    category: 'Health',
    dose: '100%',
    doseUnit: 'RDA + therapeutic',
    whyEffective:
      'Iron-free formulation tuned for male physiology, with zinc, selenium, and methylated B-vitamins to support testosterone, recovery, and metabolic function. No cheap synthetics — only the forms your body absorbs.',
    shortDescription:
      'A multi designed around what men actually need — built for performance and longevity.',
    status: 'available',
    bottleColor: 'charcoal',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/nyvNBX1SLqdHcviFnd4JS4/ChatGPT_Image_May_10,_2026,_12_31_04_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/nyvNBX1SLqdHcviFnd4JS4/ChatGPT_Image_May_10,_2026,_12_31_04_AM.png',
    price: 4499,
    lookupKey: 'multi_mens_v1',
  },
  {
    id: 'vitamin-d-k2',
    name: 'Vitamin D3 + K2',
    tagline: 'Bone, immune, and metabolic support.',
    category: 'Health',
    dose: '5,000',
    doseUnit: 'IU D3 + 100mcg K2',
    whyEffective:
      'Therapeutic 5,000 IU D3 paired with K2 MK-7 — directing calcium to bones rather than arteries. Far above the inadequate 400-1000 IU in standard multis.',
    shortDescription:
      'The synergistic pair most people are deficient in — finally at a dose that moves the needle.',
    status: 'available',
    bottleColor: 'tealslate',
    accentColor: 'lightteal',
    image:
      'https://cdn.magicpatterns.com/uploads/vNPgGgsAVg6qH4KZ46uGCU/ChatGPT_Image_May_10,_2026,_12_30_59_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/vNPgGgsAVg6qH4KZ46uGCU/ChatGPT_Image_May_10,_2026,_12_30_59_AM.png',
    price: 2499,
    lookupKey: 'vitamin_d_k2_v1',
  },
  {
    id: 'omega-3',
    name: 'Omega-3 EPA/DHA',
    tagline: 'High-potency. Low oxidation.',
    category: 'Health',
    dose: '2,400',
    doseUnit: 'mg EPA+DHA',
    whyEffective:
      'Most fish oils provide 300-600mg EPA/DHA per softgel — meaning 4-8 pills daily for an effective dose. Ours delivers 2,400mg combined in just 2 softgels.',
    shortDescription:
      'Cardiovascular, brain, and inflammatory support — third-party tested for purity and oxidation.',
    status: 'available',
    bottleColor: 'navyslate',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/iAGp26JM7qMRvz8YtMXcEj/ChatGPT_Image_May_10,_2026,_12_30_53_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/iAGp26JM7qMRvz8YtMXcEj/ChatGPT_Image_May_10,_2026,_12_30_53_AM.png',
    price: 3999,
    lookupKey: 'omega_3_v1',
  },
  {
    id: 'ashwagandha',
    name: 'Ashwagandha (KSM-66)',
    tagline: 'Stress modulation, clinically validated.',
    category: 'Stress',
    dose: '600',
    doseUnit: 'mg KSM-66 extract',
    whyEffective:
      "KSM-66 is the most clinically studied ashwagandha extract. We use the full 600mg dose proven to reduce cortisol and improve stress markers — not the underdosed 300mg you'll see elsewhere.",
    shortDescription:
      'The adaptogen for cortisol management, sleep quality, and testosterone support.',
    status: 'available',
    bottleColor: 'burgundy',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/tBY1DyTBs2gW5y29hhLaxG/ChatGPT_Image_May_10,_2026,_12_30_44_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/tBY1DyTBs2gW5y29hhLaxG/ChatGPT_Image_May_10,_2026,_12_30_44_AM.png',
    price: 3499,
    lookupKey: 'ashwagandha_v1',
  },
  {
    id: 'melatonin',
    name: 'Low-Dose Melatonin',
    tagline: 'Less is more, scientifically.',
    category: 'Sleep',
    dose: '0.3',
    doseUnit: 'mg sublingual',
    whyEffective:
      'Counterintuitively, 0.3mg works better than the 5-10mg sold everywhere — closer to physiological levels, fewer next-day grogginess, no receptor desensitization.',
    shortDescription:
      'The dose your body actually produces — for sleep onset without the hangover.',
    status: 'available',
    bottleColor: 'charcoal',
    accentColor: 'tealslate',
    image:
      'https://cdn.magicpatterns.com/uploads/tP9jUNHcWeb1aPJyK3wgD1/ChatGPT_Image_May_10,_2026,_12_30_39_AM.png',
    backImage:
      'https://cdn.magicpatterns.com/uploads/tP9jUNHcWeb1aPJyK3wgD1/ChatGPT_Image_May_10,_2026,_12_30_39_AM.png',
    price: 1999,
    lookupKey: 'melatonin_v1',
  },
]
